/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectVisualFilters,
  selectHotelName,
  selectPmsSync,
  selectTrendBookingDate,
  selectTrendTargetDate,
  selectWeeklyTrendEndDate,
  selectWeeklyTrendStartDate,
  // selectHotelDropdownList,
  selectIsDemoUser,
  selectEnableHotelGroups,
  // selectHotelGroupDropdownList,
  selectReportType,
  selectTabValue,
  selectIsPrimaryHotelSetOverTime,
  selectInvalidPrimaryHotelList,
  selectTabList,
} from 'modules/dashboard/selectors';
import {
  selectIsCumulative,
  selectTrendTimeWeeklyView,
  selectTrendSegmentFocusOn,
  selectTrendHotelNormalizedView,
  selectTrendHotelFocusOn,
  selectEnableSecondaryHotelGroups,
  selectTrendSegmentChartBreakdown,
  selectIsSegmentDetailed,
  selectIsForecast,
  selectActiveTrendTab,
  selectAdjustedEndDate,
  selectGranularity,
  selectTabularView,
  selectAdjustedStartDate,
  selectIsMovingAverage,
  selectMovingAverage,
  selectIsFocusOnSet,
  selectIsFocusAllSelected,
  selectTrendEndDate,
  selectTrendStartDate,
  selectIsSecondaryHotelSetOverTime,
  selectInvalidSecondaryHotelList,
} from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import { trendActions } from 'modules/dashboard/components/tab-container/trend-tabs/slice';
import {
  getBookingDateSchema,
  getDashboardPmsSyncSchema,
  getGranularitySchema,
  getGroupASchema,
  getGroupHotelFilterSchema,
  getHotelGroupASchema,
  getHotelGroupOrderSchema,
  getHotelGroupSchema,
  getHotelIdSchema,
  getHotelNameOrderSchema,
  getIsForecastSchema,
  getMovingAverageSchema,
  getNormalizedViewSchema,
  getPmsForecastSchema,
  getPmsSyncToggleSchema,
  getSegmentFocusOnSchema,
  getTargetDateSchema,
  getTrendHotelPmsSyncToggleSchema,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';
import { ENVIRONMENT } from 'config';
import { getSelectedBreakdown } from 'modules/dashboard/functions';
import { REPORT_TYPE } from 'modules/dashboard/constants';
import writeLog from 'modules/common/utils/filter-log';
import { GRANULARITY } from '../constants';
/**
 * Custom hook to generate Trend Hotel widget filter schemas
 */
const useTrendHotel = () => {
  const dispatch = useDispatch();
  //
  const bookingDate = useSelector(selectTrendBookingDate);
  const targetDate = useSelector(selectTrendTargetDate);
  const hotelId = useSelector(selectHotelName);
  const visualFilters = useSelector(selectVisualFilters);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const weeklyViewToggle = useSelector(selectTrendTimeWeeklyView);
  const targetStartDate = useSelector(selectWeeklyTrendStartDate);
  const targetEndDate = useSelector(selectWeeklyTrendEndDate);
  const isCumulative = useSelector(selectIsCumulative);
  const focusOn = useSelector(selectTrendSegmentFocusOn);
  const normalizedViewToggle = useSelector(selectTrendHotelNormalizedView);
  // const hotelDropdownList = useSelector(selectHotelDropdownList);
  const isDemoUser = useSelector(selectIsDemoUser);
  const hotelFocusOn = useSelector(selectTrendHotelFocusOn);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const chartBreakdown = useSelector(selectTrendSegmentChartBreakdown);
  const isDetailed = useSelector(selectIsSegmentDetailed);
  const isTrendForecast = useSelector(selectIsForecast);
  const activeTab = useSelector(selectActiveTrendTab);
  const reportType = useSelector(selectReportType);
  const isTabular = useSelector(selectTabularView);
  const adjustedStartDate = useSelector(selectAdjustedStartDate);
  const adjustedEndDate = useSelector(selectAdjustedEndDate);
  const granularity = useSelector(selectGranularity);
  const isMovingAverage = useSelector(selectIsMovingAverage);
  const movingAverage = useSelector(selectMovingAverage);
  const tabValue = useSelector(selectTabValue);
  const IsFocusOnSet = useSelector(selectIsFocusOnSet);
  const isFocusAllSelected = useSelector(selectIsFocusAllSelected);
  const startDate = useSelector(selectTrendStartDate);
  const endDate = useSelector(selectTrendEndDate);
  const isPrimaryHotelSetOverTime = useSelector(selectIsPrimaryHotelSetOverTime);
  const invalidPrimaryHotels = useSelector(selectInvalidPrimaryHotelList);
  const isSecondaryHotelSetOverTime = useSelector(selectIsSecondaryHotelSetOverTime);
  const invalidSecondaryHotels = useSelector(selectInvalidSecondaryHotelList);
  const TAB_VALUE = useSelector(selectTabList);
  //
  const [previousHotel, setPreviousHotel] = useState(null);
  //
  const serializedState = JSON.parse(localStorage.getItem(`persist:${ENVIRONMENT.PERSIST_KEY}`));
  const persistValues = Object.values(serializedState)[1];
  const persistedHotelName = JSON.parse(persistValues).hotelName;
  //
  useEffect(() => {
    // Persist hotel focus on value on browser refresh
    setPreviousHotel(persistedHotelName);
    if (previousHotel !== null) {
      // dispatch(trendActions.setTrendHotelFocusOn(hotelDropdownList[0]));
    }
  }, [hotelId]);
  //
  useEffect(() => {
    // Calculate adjusted stay date
    if (isTabular) {
      if (weeklyViewToggle) {
        dispatch(trendActions.setTrendStartDate(targetStartDate));
        dispatch(trendActions.setTrendEndDate(targetEndDate));
      } else if (granularity === GRANULARITY.DAILY) {
        dispatch(trendActions.setTrendStartDate(targetDate?.startDate));
        dispatch(trendActions.setTrendEndDate(targetDate?.endDate));
      } else {
        dispatch(trendActions.setTrendStartDate(adjustedStartDate));
        dispatch(trendActions.setTrendEndDate(adjustedEndDate));
      }
    } else if (isCumulative) {
      dispatch(trendActions.setTrendStartDate(targetDate?.startDate));
      dispatch(trendActions.setTrendEndDate(targetDate?.endDate));
    } else {
      dispatch(
        trendActions.setTrendStartDate(weeklyViewToggle ? targetStartDate : targetDate?.startDate)
      );
      dispatch(
        trendActions.setTrendEndDate(weeklyViewToggle ? targetEndDate : targetDate?.endDate)
      );
    }
  }, [
    isTabular,
    granularity,
    isCumulative,
    weeklyViewToggle,
    targetStartDate,
    targetEndDate,
    targetDate,
    adjustedEndDate,
    adjustedStartDate,
  ]);
  // Generate filter list for trend hotel visuals
  useEffect(() => {
    if (
      !IsFocusOnSet ||
      !(tabValue === TAB_VALUE.TREND_TIME || tabValue === TAB_VALUE.PBI_TREND) ||
      reportType !== REPORT_TYPE.TREND_TIME ||
      !targetDate ||
      !bookingDate ||
      !visualFilters ||
      !hotelId ||
      !chartBreakdown
    ) {
      return;
    }
    const isForecastFilter = getIsForecastSchema(visualFilters, isTrendForecast);
    const pmsSyncForecastFilter = getPmsForecastSchema(visualFilters, pmsSyncToggle);
    const targetDateFilter = getTargetDateSchema(visualFilters, startDate, endDate);
    const bookingDateFilter = getBookingDateSchema(
      visualFilters,
      bookingDate?.startDate,
      bookingDate?.endDate
    );
    let hotelGroup1Filter;
    let hotelListIds = [];
    // Extract invalid primary IDs
    const invalidPrimaryIds = invalidPrimaryHotels.map((hotel) => hotel.hotelCode);

    if (hotelId?.hotels) {
      if (isPrimaryHotelSetOverTime) {
        hotelListIds = hotelId.hotels
          .filter((hotel) => !invalidPrimaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        hotelListIds = hotelId?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
    }

    // Extract invalid secondary IDs
    const invalidSecondaryIds = invalidSecondaryHotels.map((hotel) => hotel.hotelCode);

    let groupFocusOnIds = [];
    if (hotelFocusOn?.hotels) {
      if (isSecondaryHotelSetOverTime) {
        groupFocusOnIds = hotelFocusOn.hotels
          .filter((hotel) => !invalidSecondaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        groupFocusOnIds = hotelFocusOn?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
    } //
    const groupHotelFilter = getGroupHotelFilterSchema(visualFilters);
    if (enableHotelGroups) {
      hotelGroup1Filter = getHotelGroupSchema(hotelListIds, groupHotelFilter);
    } else {
      hotelGroup1Filter = getHotelGroupSchema([hotelId?.hotelCode], groupHotelFilter);
    }
    //
    const groupAFilter = getGroupASchema(visualFilters);
    let hotelGroupAFilter;
    if (enableSecondaryHotelGroups) {
      hotelGroupAFilter = getHotelGroupASchema(groupFocusOnIds, groupAFilter);
    } else {
      hotelGroupAFilter = getHotelGroupASchema([hotelFocusOn?.hotelCode], groupAFilter);
    }
    //
    let dimHotelIds = [];
    if (enableHotelGroups) {
      if (enableSecondaryHotelGroups) {
        dimHotelIds = new Set([...groupFocusOnIds, ...hotelListIds]);
      } else {
        dimHotelIds = new Set([hotelFocusOn?.hotelCode, ...hotelListIds]);
      }
    } else if (enableSecondaryHotelGroups) {
      dimHotelIds = new Set([...groupFocusOnIds, hotelId?.hotelCode]);
    } else {
      dimHotelIds = new Set([hotelFocusOn?.hotelCode, hotelId?.hotelCode]);
    }
    //
    const secondaryHotelIdFilter = getHotelIdSchema(Array.from(dimHotelIds), visualFilters);
    const hotelNameOrderFilter = getHotelNameOrderSchema(visualFilters, isDemoUser);
    const hotelGroupOrderFilter = getHotelGroupOrderSchema(visualFilters, isDemoUser);
    const pmsSyncFilter = getPmsSyncToggleSchema(visualFilters, pmsSyncToggle);
    const trendHotelPmsSyncFilter = getTrendHotelPmsSyncToggleSchema(visualFilters, pmsSyncToggle);
    const normalizedFilter = getNormalizedViewSchema(visualFilters, normalizedViewToggle);
    //
    const hotelFilters = [secondaryHotelIdFilter, hotelGroupAFilter, hotelGroup1Filter];
    const selectedBreakdown = getSelectedBreakdown(visualFilters, chartBreakdown, isDetailed);
    const segmentFocusOnFilter = getSegmentFocusOnSchema(
      selectedBreakdown,
      focusOn?.map((item) => item?.label)
    );
    const granularityFilter = getGranularitySchema(visualFilters, granularity);
    const movingAverageFilter = getMovingAverageSchema(visualFilters, movingAverage);
    const pmsSyncSchema = getDashboardPmsSyncSchema(pmsSyncToggle);
    //
    if (bookingDateFilter && targetDateFilter && hotelFilters && segmentFocusOnFilter) {
      let filters = [
        ...pmsSyncFilter,
        bookingDateFilter,
        targetDateFilter,
        hotelNameOrderFilter,
        ...pmsSyncForecastFilter,
        hotelGroupOrderFilter,
        ...hotelFilters,
        ...segmentFocusOnFilter,
        isForecastFilter,
        ...pmsSyncSchema,
        ...trendHotelPmsSyncFilter,
      ];
      if (isMovingAverage) {
        filters = [...filters, movingAverageFilter];
      }
      if (activeTab === REPORT_TYPE.TREND_HOTEL) {
        //
        writeLog('V2 Trend Hotel Filters', filters);
      }
      dispatch(trendActions.updateTrendHotelFilterList(filters));
      if (normalizedFilter) {
        filters = [...filters, normalizedFilter];
        if (activeTab === REPORT_TYPE.TREND_HOTEL) {
          //
          writeLog('V2 Trend Hotel Normalized Filters', filters);
        }
        dispatch(trendActions.setTrendHotelNormalizedFilterList(filters));
      }
      // tabular filter list
      let tabularFilters = [
        ...pmsSyncSchema,
        ...pmsSyncFilter,
        ...trendHotelPmsSyncFilter,
        bookingDateFilter,
        targetDateFilter,
        hotelGroupOrderFilter,
        ...hotelFilters,
        normalizedFilter,
        movingAverageFilter,
        ...segmentFocusOnFilter,
        isForecastFilter,
      ];
      if (!weeklyViewToggle) {
        tabularFilters = [...tabularFilters, granularityFilter];
      }
      //
      if (activeTab === REPORT_TYPE.TREND_HOTEL) {
        writeLog('v2 Trend Hotel Tabular Filters', tabularFilters);
      }
      dispatch(trendActions.updateTrendHotelTabularFilterList(tabularFilters));
    }
  }, [
    bookingDate,
    visualFilters,
    targetDate,
    hotelId,
    pmsSyncToggle,
    hotelFocusOn,
    weeklyViewToggle,
    targetStartDate,
    targetEndDate,
    isCumulative,
    normalizedViewToggle,
    enableHotelGroups,
    enableSecondaryHotelGroups,
    chartBreakdown,
    isDetailed,
    focusOn,
    isTrendForecast,
    reportType,
    activeTab,
    previousHotel,
    granularity,
    adjustedStartDate,
    adjustedEndDate,
    isTabular,
    isMovingAverage,
    movingAverage,
    tabValue,
    IsFocusOnSet,
    isFocusAllSelected,
    startDate,
    endDate,
    isPrimaryHotelSetOverTime,
    invalidPrimaryHotels,
    isSecondaryHotelSetOverTime,
    invalidSecondaryHotels,
  ]);
};
//
export default useTrendHotel;
